import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "./firebase";

export default function VetConsultationPopup({ user, role }) {
  const normalizedRole = String(role || "").trim().toLowerCase();
  const userEmail = String(user?.email || "").trim();
  const emailVariants = useMemo(
    () => [...new Set([userEmail, userEmail.toLowerCase()].filter(Boolean))],
    [userEmail],
  );
  const [requests, setRequests] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [actioningId, setActioningId] = useState("");

  useEffect(() => {
    if (normalizedRole !== "provider" || !emailVariants.length) {
      setRequests([]);
      setLoadError("");
      return undefined;
    }

    const bucketByVariant = new Map(emailVariants.map((variant) => [variant, new Map()]));
    const applyRows = () => {
      const merged = new Map();
      bucketByVariant.forEach((bucket) => {
        bucket.forEach((row, id) => merged.set(id, row));
      });
      const rows = [...merged.values()]
        .filter((item) =>
          String(item.type || "").trim().toLowerCase() === "vet_consultation_request" &&
          item.read !== true &&
          String(item.bookingId || "").trim(),
        )
        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setRequests(rows.slice(0, 5));
    };

    const unsubscribers = emailVariants.map((variant) => {
      const notificationsQuery = query(
        collection(db, "notifications"),
        where("recipientEmail", "==", variant),
      );
      return onSnapshot(
        notificationsQuery,
        (snapshot) => {
          const bucket = bucketByVariant.get(variant);
          if (!bucket) return;
          bucket.clear();
          snapshot.docs.forEach((row) => {
            bucket.set(row.id, {id: row.id, ...row.data()});
          });
          applyRows();
          setLoadError("");
        },
        (error) => {
          setLoadError(error.message || "Unable to load consultation requests.");
          applyRows();
        },
      );
    });

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [emailVariants, normalizedRole]);

  const activeRequest = requests[0] || null;

  const markRequestRead = async (notificationId = "") => {
    const safeId = String(notificationId || "").trim();
    if (!safeId) return;
    await updateDoc(doc(db, "notifications", safeId), {
      read: true,
    });
  };

  const openConsultation = async () => {
    if (!activeRequest?.id) return;
    const bookingId = String(activeRequest.bookingId || "").trim();
    if (!bookingId) return;
    try {
      setActioningId(activeRequest.id);
      await markRequestRead(activeRequest.id);
      window.location.hash = `#/consultation/${encodeURIComponent(bookingId)}`;
    } catch (error) {
      setLoadError(error.message || "Unable to open consultation.");
    } finally {
      setActioningId("");
    }
  };

  const dismissRequest = async () => {
    if (!activeRequest?.id) return;
    try {
      setActioningId(activeRequest.id);
      await markRequestRead(activeRequest.id);
    } catch (error) {
      setLoadError(error.message || "Unable to dismiss consultation request.");
    } finally {
      setActioningId("");
    }
  };

  if (normalizedRole !== "provider" || !activeRequest) return null;

  return (
    <div
      style={{
        position: "fixed",
        right: 16,
        bottom: 16,
        width: "min(380px, calc(100vw - 24px))",
        border: "1px solid #bfdbfe",
        borderRadius: 12,
        background: "#eff6ff",
        boxShadow: "0 14px 32px rgba(15, 23, 42, 0.22)",
        padding: 12,
        zIndex: 1200,
      }}
    >
      <div style={{ fontWeight: 800, color: "#1e40af", marginBottom: 6 }}>
        New Vet Consultation Request
      </div>
      <div style={{ color: "#1e293b", marginBottom: 8 }}>
        {String(activeRequest.message || "A customer is waiting for online consultation.")}
      </div>
      <div style={{ color: "#334155", fontSize: 12, marginBottom: 8 }}>
        Booking ID: {String(activeRequest.bookingId || "").slice(0, 12)}
        {activeRequest.bookingDate ? ` • Date: ${String(activeRequest.bookingDate)}` : ""}
      </div>
      {loadError ? (
        <div style={{ color: "#991b1b", fontSize: 12, marginBottom: 8 }}>
          {loadError}
        </div>
      ) : null}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={openConsultation}
          disabled={actioningId === activeRequest.id}
          style={{
            border: "1px solid #1d4ed8",
            background: "#1d4ed8",
            color: "#fff",
            borderRadius: 8,
            padding: "8px 12px",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          {actioningId === activeRequest.id ? "Opening..." : "Attend Now"}
        </button>
        <button
          type="button"
          onClick={dismissRequest}
          disabled={actioningId === activeRequest.id}
          style={{
            border: "1px solid #94a3b8",
            background: "#fff",
            color: "#0f172a",
            borderRadius: 8,
            padding: "8px 12px",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
